/**
 * Meta / Instagram In-App Browser Mobile Safe Area Guard
 *
 * Prevents native Meta bottom overlays ("Call now", "Send message", webview chrome)
 * from obscuring the mobile cart drawer checkout button.
 *
 * Strictly scoped to Instagram / Facebook in-app browsers on mobile screens (< 750px).
 * Zero side-effects for standard browsers (Chrome, Safari, Edge, etc.) or desktop.
 */
(() => {
  'use strict';

  const MIN_CLEARANCE = 112; // Base clearance for Meta native CTA
  const EXTRA_GAP = 8;       // Spacing buffer above native bar
  const MAX_CLEARANCE = 180; // Safety upper clamp to prevent drawer compression
  const MOBILE_BREAKPOINT = 750;

  let rafId = null;

  function detectMetaInApp() {
    try {
      const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
      const isInstagram = ua.includes('instagram');
      const isFacebook = ua.includes('fban') || ua.includes('fbav') || ua.includes('fb_iab');
      return {
        isMeta: isInstagram || isFacebook,
        isInstagram,
        isFacebook
      };
    } catch (e) {
      return { isMeta: false, isInstagram: false, isFacebook: false };
    }
  }

  function calculateClearance() {
    let clearance = MIN_CLEARANCE + EXTRA_GAP; // Default 120px

    if (window.visualViewport && typeof window.visualViewport.height === 'number') {
      try {
        const vv = window.visualViewport;
        const viewportDiff = Math.max(0, window.innerHeight - Math.round(vv.height));
        if (viewportDiff > 0) {
          clearance = Math.max(clearance, viewportDiff + EXTRA_GAP);
        }
      } catch (e) {
        // Fallback to default clearance if visualViewport calculation fails
      }
    }

    // Clamp between minimum safe gap and maximum allowed clearance
    return Math.min(MAX_CLEARANCE, Math.max(MIN_CLEARANCE + EXTRA_GAP, Math.round(clearance)));
  }

  function update() {
    rafId = null;

    try {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      const { isMeta, isInstagram, isFacebook } = detectMetaInApp();
      const docEl = document.documentElement;

      if (isMeta && isMobile) {
        docEl.classList.add('bodf-meta-inapp');
        if (isInstagram) {
          docEl.classList.add('bodf-instagram-inapp');
        } else {
          docEl.classList.remove('bodf-instagram-inapp');
        }

        if (isFacebook) {
          docEl.classList.add('bodf-facebook-inapp');
        } else {
          docEl.classList.remove('bodf-facebook-inapp');
        }

        const safeClearance = calculateClearance();
        docEl.style.setProperty('--bodf-meta-bottom-clearance', `${safeClearance}px`);
      } else {
        // Non-Meta or Desktop (>= 750px): completely reset
        docEl.classList.remove('bodf-meta-inapp', 'bodf-instagram-inapp', 'bodf-facebook-inapp');
        docEl.style.removeProperty('--bodf-meta-bottom-clearance');
      }
    } catch (e) {
      // Fail silently without breaking execution
    }
  }

  function scheduleUpdate() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(update);
  }

  // Initial execution
  update();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', update, { once: true });
  }

  // Passive event listeners for viewport, orientation, and visibility changes
  window.addEventListener('resize', scheduleUpdate, { passive: true });
  window.addEventListener('orientationchange', scheduleUpdate, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      scheduleUpdate();
    }
  }, { passive: true });

  if (window.visualViewport) {
    try {
      window.visualViewport.addEventListener('resize', scheduleUpdate, { passive: true });
      window.visualViewport.addEventListener('scroll', scheduleUpdate, { passive: true });
    } catch (e) {
      // Passive listener unsupported fallback
    }
  }
})();
